import React, { useState, useEffect, memo } from 'react';
import { Box, IconButton, Badge } from '@mui/material';
import { ArrowDown } from 'lucide-react';

const ScrollableChatContainer = memo(({
  chatContainerRef,
  messages,
  darkMode,
  scrollToBottom,
  children
}) => {
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [newMessageCount, setNewMessageCount] = useState(0);

  // Scroll listener logic moved here
  useEffect(() => {
    const container = chatContainerRef.current;
    if (!container) return;

    let throttleTimeout = null;
    const THROTTLE_DELAY = 150; // ms

    const handleScroll = () => {
      if (throttleTimeout) {
        return;
      }

      throttleTimeout = setTimeout(() => {
        throttleTimeout = null;
        if (!chatContainerRef.current) return;

        const currentContainer = chatContainerRef.current;
        const isNearBottom = currentContainer.scrollHeight - currentContainer.scrollTop - currentContainer.clientHeight < 100;
        
        setShowScrollButton(currentShowState => {
          const nextShowState = !isNearBottom && messages.length > 3;
          return currentShowState !== nextShowState ? nextShowState : currentShowState;
        });

        if (isNearBottom) {
          setNewMessageCount(prevCount => (prevCount !== 0 ? 0 : prevCount));
        }
      }, THROTTLE_DELAY);
    };

    const initialCheck = () => {
      if (!chatContainerRef.current) return;
      const currentContainer = chatContainerRef.current;
      const isNearBottom = currentContainer.scrollHeight - currentContainer.scrollTop - currentContainer.clientHeight < 100;
      setShowScrollButton(!isNearBottom && messages.length > 3);
      if (isNearBottom) {
        setNewMessageCount(0);
      }
    };

    container.addEventListener('scroll', handleScroll);
    initialCheck(); // Run initial check

    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (throttleTimeout) {
        clearTimeout(throttleTimeout);
      }
    };
  }, [messages.length, chatContainerRef]); // Dependencies

  // Effect to track new messages when not scrolled to bottom
  useEffect(() => {
      const container = chatContainerRef.current;
      if (!container) return;
      
      const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
      
      // Increment count only if a new message arrives AND we are not near the bottom
      if (!isNearBottom && messages.length > 0) {
          const lastMessage = messages[messages.length - 1];
          // Check if the last message is from the server and wasn't the one triggering the initial load
          if (lastMessage?.sender === 'server') { 
              // We might need a more sophisticated check if initial load adds server messages
              // For now, just incrementing assuming new server messages increase count.
              setNewMessageCount(prev => prev + 1);
          }
      }
      // Note: This depends on messages changing. If messages are stable, count won't increase.
      // Reset happens via the scroll listener when user scrolls down.
  }, [messages, chatContainerRef]);


  return (
    <Box
      ref={chatContainerRef}
      sx={{
        flexGrow: 1,
        overflowY: 'auto',
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative', // Needed for absolute positioning of the button
        bgcolor: darkMode ? '#1A1A1A' : '#F8F9FA',
        // Add scrollbar styling here if desired, inherited from ChatPanel previously
        '::-webkit-scrollbar': {
          width: '8px',
          height: '8px',
        },
        '::-webkit-scrollbar-track': {
          background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
          borderRadius: '10px',
        },
        '::-webkit-scrollbar-thumb': {
          background: darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
          borderRadius: '10px',
        },
        '::-webkit-scrollbar-thumb:hover': {
          background: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
        },
      }}
    >
      {children} {/* Render the actual chat content */} 

      {/* Scroll to bottom button - rendered inside the scrollable container */} 
      {showScrollButton && (
        <Badge 
          badgeContent={newMessageCount > 0 ? newMessageCount : null} 
          color="primary"
          overlap="circular"
          sx={{ 
            position: 'absolute', // Position relative to this Box
            bottom: 16, // Adjust positioning as needed
            right: 16,
            zIndex: 10
          }}
        >
          <IconButton
            onClick={scrollToBottom} // Use the passed function
            sx={{
              bgcolor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
              '&:hover': {
                bgcolor: darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
              },
              boxShadow: 2,
            }}
            aria-label="Scroll to bottom"
          >
            <ArrowDown size={20} />
          </IconButton>
        </Badge>
      )}
    </Box>
  );
});

export default ScrollableChatContainer; 